import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { automunityAPI } from '../lib/api';
import './Automations.css';

interface Automation {
  automation_id: number;
  identifier?: string;
  name: string;
  description?: string;
  host_id?: number;
  host?: string;
  team_id?: number;
  team_name?: string;
  team_names?: string;
  cost_per_invocation?: string;
  minute_gain_per_objective?: string;
  hourly_rate_gain_per_objective?: string;
  development_cost?: string;
  date_created?: string;
  valuation_category?: string;
  application_url?: string;
  documentation_text?: string;
  documentation_diagram_url?: string;
  adl_name?: string;
  contributors?: any;
  services?: any;
  stages?: any;
  request_count?: number;
  open_request_count?: number;
  in_progress_count?: number;
  recent_request_count?: number;
  [key: string]: any;
}

interface TeamGroup {
  name: string;
  count: number;
}

interface LeadGroup {
  name: string;
  count: number;
}

// Page-level work-activity filter options.
type WorkFilter = 'all' | 'has_active' | 'in_progress' | 'recent';

const Automations: React.FC = () => {
  const [nameFilter, setNameFilter] = useState('');
  const [filterTeam, setFilterTeam] = useState<string>('all');
  const [filterLead, setFilterLead] = useState<string>('all');
  const [filterWork, setFilterWork] = useState<WorkFilter>('all');
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [teamGroups, setTeamGroups] = useState<TeamGroup[]>([]);
  const [leadGroups, setLeadGroups] = useState<LeadGroup[]>([]);
  const [allTeamsList, setAllTeamsList] = useState<Array<{team_id: number; name: string}>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'name', direction: 'asc' });

  useEffect(() => {
    const fetchAutomations = async () => {
      try {
        setLoading(true);
        const [response, teamsRes] = await Promise.all([
          automunityAPI.getAutomations(),
          automunityAPI.getTeams()
        ]);
        
        const teamsResult = await teamsRes.body.json();
        const teamsList = (teamsResult as any)?.records || (Array.isArray(teamsResult) ? teamsResult : []);
        setAllTeamsList(Array.isArray(teamsList) ? teamsList.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '')) : []);
        
        const result = await response.body.json();
        let raw: any[];
        if (Array.isArray(result)) raw = result;
        else if (Array.isArray((result as any).records)) raw = (result as any).records;
        else if (typeof (result as any).records === 'string') { try { raw = JSON.parse((result as any).records); } catch { raw = []; } }
        else raw = [];

        const valid = raw.filter((a: any) => a && a.automation_id).map((a: any) => ({
          ...a,
          contributors: typeof a.contributors === 'string' ? JSON.parse(a.contributors) : a.contributors,
          services: typeof a.services === 'string' ? JSON.parse(a.services) : a.services,
          stages: typeof a.stages === 'string' ? JSON.parse(a.stages) : a.stages,
        }));

        setAutomations(valid);

        // Build team counts
        const countMap = new Map<string, number>();
        valid.forEach((a: Automation) => {
          const teams = a.team_names
            ? a.team_names.split(',').map(t => t.trim()).filter(Boolean)
            : a.team_name ? [a.team_name] : ['Unassigned'];
          teams.forEach(t => countMap.set(t, (countMap.get(t) || 0) + 1));
        });
        const groups = Array.from(countMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => a.name === 'Unassigned' ? 1 : b.name === 'Unassigned' ? -1 : a.name.localeCompare(b.name));
        setTeamGroups(groups);

        // Build lead (ADL) counts
        const leadMap = new Map<string, number>();
        valid.forEach((a: Automation) => {
          const lead = (a.adl_name && a.adl_name.trim()) ? a.adl_name.trim() : 'Unassigned';
          leadMap.set(lead, (leadMap.get(lead) || 0) + 1);
        });
        const leads = Array.from(leadMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => a.name === 'Unassigned' ? 1 : b.name === 'Unassigned' ? -1 : a.name.localeCompare(b.name));
        setLeadGroups(leads);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load automations');
      } finally {
        setLoading(false);
      }
    };
    fetchAutomations();
  }, []);

  const handleSearch = () => {
    window.location.href = `/chat?prompt=${encodeURIComponent(nameFilter)}`;
  };

  // Filter automations by team, lead, active-work status, and free text
  const filteredAutomations = automations.filter(a => {
    // Team filter
    if (filterTeam !== 'all') {
      const teams = a.team_names
        ? a.team_names.split(',').map(t => t.trim())
        : a.team_name ? [a.team_name] : ['Unassigned'];
      if (!teams.includes(filterTeam)) return false;
    }
    // Lead (ADL) filter
    if (filterLead !== 'all') {
      const lead = (a.adl_name && a.adl_name.trim()) ? a.adl_name.trim() : 'Unassigned';
      if (lead !== filterLead) return false;
    }
    // Work-activity filter
    if (filterWork === 'has_active' && !(a.open_request_count && a.open_request_count > 0)) return false;
    if (filterWork === 'in_progress' && !(a.in_progress_count && a.in_progress_count > 0)) return false;
    if (filterWork === 'recent' && !(a.recent_request_count && a.recent_request_count > 0)) return false;
    // Free-text filter: match on name OR lead name
    if (nameFilter) {
      const q = nameFilter.toLowerCase();
      const matchesName = a.name.toLowerCase().includes(q);
      const matchesLead = (a.adl_name || '').toLowerCase().includes(q);
      if (!matchesName && !matchesLead) return false;
    }
    return true;
  });

  // Sort
  const sortedAutomations = [...filteredAutomations].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal: any = (a as any)[key];
    let bVal: any = (b as any)[key];
    if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key: string) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleTeamChange = async (automationId: number, newTeamId: number) => {
    try {
      await automunityAPI.addAutomationTeam(String(automationId), newTeamId, true);
      window.location.reload();
    } catch (err) {
      console.error('Error updating team:', err);
    }
  };

  if (loading) return <div className="requests-loading">Loading automations...</div>;
  if (error) return <div className="requests-loading">Error: {error}</div>;

  const SortHeader: React.FC<{ label: string; sortKey: string }> = ({ label, sortKey }) => (
    <th onClick={() => handleSort(sortKey)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortConfig.key === sortKey ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  return (
    <div className="requests-container">
      <div className="requests-header">
        <h1>Automations</h1>
        <div className="requests-controls">
          <div className="filters">
            <input
              type="text"
              placeholder="Search name or lead..."
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
            />
            <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)}>
              <option value="all">All Teams ({automations.length})</option>
              {teamGroups.map(g => (
                <option key={g.name} value={g.name}>{g.name} ({g.count})</option>
              ))}
            </select>
            <select value={filterLead} onChange={e => setFilterLead(e.target.value)}>
              <option value="all">All Leads</option>
              {leadGroups.map(g => (
                <option key={g.name} value={g.name}>{g.name} ({g.count})</option>
              ))}
            </select>
            <select value={filterWork} onChange={e => setFilterWork(e.target.value as WorkFilter)}>
              <option value="all">Any Activity</option>
              <option value="has_active">Has Open Requests</option>
              <option value="in_progress">Being Worked On Now</option>
              <option value="recent">Active in Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Automations Table */}
      <div className="requests-table-container">
        <table className="requests-table">
          <thead>
            <tr>
              <SortHeader label="ID" sortKey="automation_id" />
              <SortHeader label="Name" sortKey="name" />
              <th>Description</th>
              <th>Team</th>
              <SortHeader label="Lead" sortKey="adl_name" />
              <th>Stage</th>
              <th>Contributors</th>
              <SortHeader label="Open" sortKey="open_request_count" />
              <SortHeader label="Active" sortKey="in_progress_count" />
              <SortHeader label="Recent" sortKey="recent_request_count" />
              <SortHeader label="Total" sortKey="request_count" />
              <th>Manage</th>
            </tr>
          </thead>
          <tbody>
            {sortedAutomations.length === 0 ? (
              <tr><td colSpan={12} className="no-data">No automations found</td></tr>
            ) : (
              sortedAutomations.map(a => (
                <tr key={a.automation_id}>
                  <td>{a.automation_id}</td>
                  <td style={{ fontWeight: 500 }}>
                    <Link to={`/automations/view/${a.automation_id}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                      {a.name}
                    </Link>
                  </td>
                  <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666', fontSize: '13px' }}>
                    {a.description || '—'}
                  </td>
                  <td>
                    <TeamSelector
                      automationId={a.automation_id}
                      currentTeamId={a.team_id}
                      currentTeamName={a.team_name}
                      teamNames={a.team_names}
                      allTeams={allTeamsList}
                      onTeamChange={handleTeamChange}
                    />
                  </td>
                  <td style={{ fontSize: '13px' }}>{a.adl_name || '—'}</td>
                  <td>
                    {(() => {
                      const stages = a.stages;
                      if (stages && Array.isArray(stages) && stages.length > 0) {
                        const latest = stages[stages.length - 1];
                        return <span className={`stage-badge ${(latest.name || '').toLowerCase()}`}>{latest.name}</span>;
                      }
                      return <span className="stage-badge unknown">—</span>;
                    })()}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {a.contributors && Array.isArray(a.contributors) ? a.contributors.length : 0}
                  </td>
                  <td style={{ textAlign: 'center' }}>{a.open_request_count || 0}</td>
                  <td style={{ textAlign: 'center' }}>
                    {a.in_progress_count && a.in_progress_count > 0 ? (
                      <span style={{ background: '#e6f4ea', color: '#1e7e34', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
                        {a.in_progress_count}
                      </span>
                    ) : (
                      <span style={{ color: '#bbb' }}>0</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>{a.recent_request_count || 0}</td>
                  <td style={{ textAlign: 'center', color: '#666' }}>{a.request_count || 0}</td>
                  <td>
                    <Link to={`/automations/view/${a.automation_id}`} className="btn btn-primary btn-sm">
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// TeamSelector: inline multi-team badge display with add dropdown
interface TeamSelectorProps {
  automationId: number;
  currentTeamId?: number;
  currentTeamName?: string;
  teamNames?: string;
  allTeams: Array<{team_id: number; name: string}>;
  onTeamChange: (automationId: number, teamId: number) => void;
}

const TeamSelector: React.FC<TeamSelectorProps> = ({ automationId, currentTeamId, currentTeamName, teamNames, allTeams, onTeamChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const assignedTeamNames = teamNames 
    ? teamNames.split(',').map(t => t.trim()).filter(Boolean)
    : (currentTeamName ? [currentTeamName] : []);

  const assignedTeamIds = assignedTeamNames.map(name => {
    const match = allTeams.find(t => t.name === name);
    return match?.team_id;
  }).filter(Boolean) as number[];

  const unassignedTeams = allTeams.filter(t => !assignedTeamIds.includes(t.team_id));

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
        {assignedTeamNames.length === 0 ? (
          <span style={{ color: '#999', fontSize: '12px' }}>—</span>
        ) : (
          assignedTeamNames.map((name, i) => (
            <span key={i} style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {name}
            </span>
          ))
        )}
        <button 
          onClick={() => setShowDropdown(!showDropdown)}
          style={{ background: 'none', border: '1px dashed #ccc', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', cursor: 'pointer', color: '#666' }}
        >+</button>
      </div>
      {showDropdown && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'white', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', padding: '4px', minWidth: '140px' }}>
          {unassignedTeams.length === 0 ? (
            <div style={{ padding: '4px 8px', color: '#999', fontSize: '12px' }}>All teams assigned</div>
          ) : (
            unassignedTeams.map(t => (
              <div key={t.team_id} onClick={() => { onTeamChange(automationId, t.team_id); setShowDropdown(false); }}
                style={{ padding: '4px 8px', cursor: 'pointer', fontSize: '12px', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f0f0f0')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >{t.name}</div>
            ))
          )}
          <div onClick={() => setShowDropdown(false)} style={{ padding: '4px 8px', cursor: 'pointer', fontSize: '11px', color: '#999', borderTop: '1px solid #eee', marginTop: '4px' }}>Close</div>
        </div>
      )}
    </div>
  );
};

export default Automations;
